// TotalAdminCommissionReport.jsx

import React, { useEffect, useState, useCallback } from 'react';
import {
  Button,
  Box,
  CircularProgress,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Paper,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { API_BASE_URL } from '../../constants/constants';
import { DataGrid } from '@mui/x-data-grid';
import { format } from 'date-fns';
import BackButton from '../../components/BackButton';
import { usePermissions } from '../../context/PermissionContext';
// [CASH-MODE] UPI / Cash filter + per-date split
import PaymentModeFilter from '../../components/PaymentModeFilter';
import CollectionTotals from '../../components/CollectionTotals';
import { formatDateIST } from '../../utils/dateFormat';
import { PAYMENT_MODES } from '../../constants/paymentModes';
import { paymentModeParam } from '../../utils/paymentMode';

const TotalAdminCommissionReport = () => {
  const [startDate, setStartDate] = useState(
    new Date(new Date().setDate(new Date().getDate() - 30))
  );
  const [endDate, setEndDate] = useState(new Date());
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogMessage, setDialogMessage] = useState('');
  const [commissionsByDate, setCommissionsByDate] = useState([]);
  const [grandTotals, setGrandTotals] = useState({});
  const [loading, setLoading] = useState(false);
  const { can } = usePermissions();
  const [paymentMode, setPaymentMode] = useState(PAYMENT_MODES.ALL); // [CASH-MODE]
  const [totalsByMode, setTotalsByMode] = useState(null); // [CASH-MODE]
  const [totalsByCategory, setTotalsByCategory] = useState(null); // whole-range collection tiles
  const [hasModeSplit, setHasModeSplit] = useState(false); // [CASH-MODE] API returned per-date upi/cash fields

  const validateDates = useCallback(() => {
    if (!startDate || !endDate) {
      setDialogMessage('Please select both start date and end date.');
      setOpenDialog(true);
      return false;
    }
    if (startDate > endDate) {
      setDialogMessage('Start date must be on or before the end date.');
      setOpenDialog(true);
      return false;
    }
    return true;
  }, [startDate, endDate]);

  const fetchCommissionData = useCallback(async () => {
    if (!validateDates()) {
      return;
    }
    setLoading(true);
    try {
      const formattedStartDate = format(startDate, 'yyyy-MM-dd');
      const formattedEndDate = format(endDate, 'yyyy-MM-dd');

      const response = await axios.post(
        `${API_BASE_URL}/adminReport/totalCommissions`,
        {
          startDate: formattedStartDate,
          endDate: formattedEndDate,
          paymentMode: paymentModeParam(paymentMode), // [CASH-MODE]
        },
        {
          headers: { 'Content-Type': 'application/json' },
        }
      );

      if (response.status === 200) {
        const data = response.data;
        const rows = (data.commissionsByDate || []).map((item, index) => ({
          id: item._id || index,
          serialNumber: index + 1,
          date: item._id ? formatDateIST(item._id) : (item.date || 'N/A'), // DD/MM/YYYY (IST day)
          totalAmount: item.totalAmount || '0.00',
          commissionPercentage: item.commissionPercentage || '0.00',
          totalIncome: item.totalIncome || '0.00',
          gstAmount: item.gstAmount || '0.00',
          balanceIncome: item.balanceIncome || '0.00',
          customerAmount: item.customerAmount || '0.00',
          customerWelfareAmount: item.customerWelfareAmount || '0.00',
          headOfficeAmount: item.headOfficeAmount || '0.00',
          stateAdminAmount: item.stateAdminAmount || '0.00',
          msbAmount: item.msbAmount || '0.00',
          // [CASH-MODE] per-date split (absent on older API)
          upiAmount: item.upiAmount,
          cashAmount: item.cashAmount,
          upiCommission: item.upiCommission,
          cashCommission: item.cashCommission,
        }));

        setCommissionsByDate(rows);
        setGrandTotals(data.grandTotals || {});
        // [CASH-MODE]
        setHasModeSplit((data.commissionsByDate || []).some((item) => item.upiAmount !== undefined || item.cashAmount !== undefined));
        setTotalsByMode(data.totalsByMode || (data.grandTotals && data.grandTotals.totalsByMode) || null);
        setTotalsByCategory(data.totalsByCategory || null);
      } else {
        setCommissionsByDate([]);
        setGrandTotals({});
        setHasModeSplit(false); // [CASH-MODE]
        setTotalsByMode(null); // [CASH-MODE]
        setTotalsByCategory(null);
      }
    } catch (error) {
      console.error('Error fetching commission data:', error);
      setCommissionsByDate([]);
      setGrandTotals({});
      setHasModeSplit(false); // [CASH-MODE]
      setTotalsByMode(null); // [CASH-MODE]
      setTotalsByCategory(null);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, validateDates, paymentMode]); // [CASH-MODE] paymentMode

  useEffect(() => {
    fetchCommissionData();
  }, [fetchCommissionData]);

  // Export CSV (permission gated)
  const handleExportCSV = () => {
    if (!can('reports.adminDaily.export')) return;
    if (commissionsByDate.length === 0) {
      setDialogMessage('No data to export.');
      setOpenDialog(true);
      return;
    }

    const headers = [
      'SL No',
      'Date',
      'Original Transaction Amount',
      'Commission %',
      'Commission amount',
      'Commission GST',
      'Balance',
      'Customer %',
      'Welfare %',
      'Company %',
      'State Head %',
      'Q-Pay Center %',
      // [CASH-MODE]
      ...(hasModeSplit ? ['UPI Amount', 'Cash Amount', 'UPI Commission', 'Cash Commission'] : []),
    ];

    const rows = commissionsByDate.map((c) => [
      c.serialNumber,
      `"${c.date}"`,
      c.totalAmount,
      c.commissionPercentage,
      c.totalIncome,
      c.gstAmount,
      c.balanceIncome,
      c.customerAmount,
      c.customerWelfareAmount,
      c.headOfficeAmount,
      c.stateAdminAmount,
      c.msbAmount,
      // [CASH-MODE]
      ...(hasModeSplit ? [c.upiAmount ?? 0, c.cashAmount ?? 0, c.upiCommission ?? 0, c.cashCommission ?? 0] : []),
    ]);

    const csvContent =
      'data:text/csv;charset=utf-8,' +
      [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `SUPER_ADMIN_Daily_Report_${format(new Date(), 'yyyy-MM-dd')}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const columns = [
    { field: 'serialNumber', headerName: 'SL No', width: 80 },
    { field: 'date', headerName: 'Date', width: 130 },
    { field: 'totalAmount', headerName: 'Original Amount (₹)', width: 170 },
    { field: 'commissionPercentage', headerName: 'Commission %', width: 130 },
    { field: 'totalIncome', headerName: 'Commission Amount (₹)', width: 180 },
    { field: 'gstAmount', headerName: 'Commission GST (₹)', width: 150 },
    { field: 'balanceIncome', headerName: 'Balance (₹)', width: 130 },
    { field: 'customerAmount', headerName: 'Customer %', width: 120 },
    { field: 'customerWelfareAmount', headerName: 'Welfare %', width: 120 },
    { field: 'headOfficeAmount', headerName: 'Company %', width: 120 },
    { field: 'stateAdminAmount', headerName: 'State Head %', width: 130 },
    { field: 'msbAmount', headerName: 'Q-Pay Center %', width: 140 },
    // [CASH-MODE] per-date UPI / Cash columns, only when the API sends them
    ...(hasModeSplit
      ? [
          { field: 'upiAmount', headerName: 'UPI Amount (₹)', width: 140 },
          { field: 'cashAmount', headerName: 'Cash Amount (₹)', width: 140 },
          { field: 'upiCommission', headerName: 'UPI Commission (₹)', width: 160 },
          { field: 'cashCommission', headerName: 'Cash Commission (₹)', width: 160 },
        ]
      : []),
  ];

  // [CASH-MODE] API totals, else sum of per-date split
  const modeTotals = totalsByMode || (hasModeSplit
    ? commissionsByDate.reduce(
        (acc, r) => {
          acc.UPI.totalAmount += Number(r.upiAmount) || 0;
          acc.CASH.totalAmount += Number(r.cashAmount) || 0;
          return acc;
        },
        { UPI: { totalAmount: 0 }, CASH: { totalAmount: 0 } }
      )
    : null);
  const modeAmountKey = modeTotals?.UPI?.totalAmount !== undefined ? 'totalAmount' : 'amount';

  return (
    <Box sx={{ position: 'relative', padding: 3 }}>
      <BackButton />
      <br /><br />
      <LocalizationProvider dateAdapter={AdapterDateFns}>
        <Paper elevation={3} sx={{ p: 3, borderRadius: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
              SUPER ADMIN Daily Report
            </Typography>

            {/* CSV Export Button (permission gated) */}
            {can('reports.adminDaily.export') && (
              <Button
                variant="contained"
                color="success"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ fontWeight: 'bold', borderRadius: 2 }}
              >
                Export CSV
              </Button>
            )}
          </Box>

          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="Start Date"
                value={startDate}
                onChange={(date) => setStartDate(date)}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DatePicker
                label="End Date"
                value={endDate}
                onChange={(date) => setEndDate(date)}
                renderInput={(params) => <TextField fullWidth {...params} />}
                maxDate={new Date()}
                inputFormat="dd-MM-yyyy"
              />
            </Grid>
            {/* [CASH-MODE] */}
            <Grid item xs={12} sm={4}>
              <PaymentModeFilter value={paymentMode} onChange={setPaymentMode} fullWidth />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                color="primary"
                onClick={fetchCommissionData}
                disabled={loading}
                sx={{ py: 1.5, px: 4, fontWeight: 'bold' }}
              >
                {loading ? <CircularProgress size={24} color="inherit" /> : 'Submit'}
              </Button>
            </Grid>
          </Grid>

          <CollectionTotals totals={totalsByCategory} showGrandTotal sx={{ mb: 2 }} />

          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={commissionsByDate}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 25, 50, 100]}
              loading={loading}
              disableSelectionOnClick
              rowHeight={60}
            />
          </Box>
        </Paper>

        <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
          <DialogTitle>Notification</DialogTitle>
          <DialogContent>
            <Typography>{dialogMessage}</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setOpenDialog(false)} color="primary">
              OK
            </Button>
          </DialogActions>
        </Dialog>
      </LocalizationProvider>
    </Box>
  );
};

export default TotalAdminCommissionReport;
